import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Card,
  CardBody,
  Col,
  Row,
  FormGroup,
  Label,
  Input,
  Collapse,
} from 'reactstrap';
import { QuantityDeliverySection } from '../../../../Components/QuantityTable';
import { PortRestrictionsTable } from '../../../../Components/PortRestriction';
import { formatDateToDDMMYYYY } from '../../../../utils/dateformatter';
import {
  displayValue,
  formatProposalDeadline,
  getContractPeriodProps,
  getContractTypeLabel,
  getPaymentCurrencyLabel,
  getProcurementProductNameId,
  getProcurementTypeLabel,
  getVisibleProcurementClauses,
  isLpgProduct,
} from '../../utils/tenderOpeningHelpers';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import { id } from 'date-fns/locale';
import Flatpickr from 'react-flatpickr';
import 'flatpickr/dist/themes/material_blue.css';
import { api } from '../../../../config';
import { getAuthToken } from '../../../../auth/tokenvalidation';
import '../../../../App.css';

const FIELD_ORDER = ['eventDate', 'startTime', 'endTime', 'location', 'agenda'];

const createTimeFlatpickrOptions = () => ({
  enableTime: true,
  noCalendar: true,
  dateFormat: 'H:i',
  time_24hr: true,
});

const START_TIME_FLATPICKR_OPTIONS = createTimeFlatpickrOptions();
const END_TIME_FLATPICKR_OPTIONS = createTimeFlatpickrOptions();

const focusFieldRef = fieldRef => {
  const target = fieldRef?.current;
  if (!target) return;
  const input = target.input ?? target;
  input.focus?.();
  input.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
};

const formatEventDate = dateStr => {
  if (!dateStr) return '-';
  try {
    const date = new Date(dateStr);
    return format(date, 'EEEE, d MMMM yyyy', { locale: id });
  } catch (error) {
    return dateStr;
  }
};

const InfoBlock = ({ label, value, col = 3, isHtml = false }) => (
  <Col xs={6} md={col}>
    <div className='border-start border-2 border-primary ps-3'>
      <div className='text-muted small fw-semibold mb-2'>{label}</div>
      {isHtml ? (
        <HtmlContent value={value} />
      ) : (
        <div className='text-dark fs-13 mb-0 lh-base'>
          {displayValue(value)}
        </div>
      )}
    </div>
  </Col>
);

const MandatoryBadge = () => (
  <span className='badge bg-danger-subtle text-danger border border-danger-subtle rounded-pill fw-medium fs-11'>
    Mandatory
  </span>
);

const HtmlContent = ({ value, framed = true }) => {
  if (!value) {
    return <div className='text-dark mb-0 lh-base'>-</div>;
  }

  const body = (
    <div
      className='html-content-body'
      dangerouslySetInnerHTML={{ __html: value }}
    />
  );

  if (!framed) return body;

  return <div className='html-content-panel'>{body}</div>;
};

const ClauseBlock = ({ index, title, value, isMandatory }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div
      className={`rounded-3 overflow-hidden mb-2 ${
        isExpanded
          ? 'border border-primary shadow-sm bg-white'
          : 'border bg-white'
      }`}
    >
      <button
        type='button'
        className='w-100 border-0 bg-transparent text-start px-3 py-3 d-flex align-items-center gap-3'
        onClick={() => setIsExpanded(prev => !prev)}
        aria-expanded={isExpanded}
      >
        <span
          className={`rounded-circle d-inline-flex align-items-center justify-content-center flex-shrink-0 fw-semibold fs-12 ${
            isExpanded ? 'bg-primary text-white' : 'bg-light text-muted border'
          }`}
          style={{ width: '28px', height: '28px' }}
        >
          {index}
        </span>

        <div className='flex-grow-1 min-w-0'>
          <div className='d-flex align-items-center gap-2 flex-wrap'>
            <span
              className={`fs-14 mb-0 ${
                isExpanded ? 'fw-bold text-dark' : 'fw-semibold text-dark'
              }`}
            >
              {title}
            </span>
            {isMandatory && <MandatoryBadge />}
          </div>
          {!isExpanded && (
            <span className='text-muted fs-12'>
              Click to view clause content
            </span>
          )}
        </div>

        <span
          className={`rounded-circle d-inline-flex align-items-center justify-content-center flex-shrink-0 ${
            isExpanded
              ? 'bg-primary-subtle text-primary'
              : 'bg-light text-muted'
          }`}
          style={{ width: '28px', height: '28px' }}
        >
          <i
            className={`ri-arrow-${isExpanded ? 'up' : 'down'}-s-line fs-16`}
          ></i>
        </span>
      </button>

      <Collapse isOpen={isExpanded}>
        <div className='px-3 pb-3'>
          <div className='ms-1 ps-4'>
            {isMandatory && (
              <div className='text-danger small fw-medium mb-2'>
                * Mandatory clause for DMUT to fulfill
              </div>
            )}
            <div className='p-3 rounded-3 bg-light-subtle border text-muted fs-13 lh-base'>
              <HtmlContent value={value} framed={false} />
            </div>
          </div>
        </div>
      </Collapse>
    </div>
  );
};

const InvitationDetailCard = ({
  procurement,
  isInviteSubmitted,
  isSubmitting,
  invitationForm,
  formErrors,
  fieldRefs,
  untilFinished,
  endTimeFlatpickrOptions,
  onFormChange,
  onUntilFinishedChange,
}) => (
  <Card className='border shadow-md mb-4'>
    <CardBody>
      <Row>
        <Col xs={12}>
          <div className='mb-2'>
            <h5 className='fw-bold text-dark mb-1'>Invitation Detail</h5>
            <p className='text-muted small'>
              Bisa Jadi Kalimat tambahan untuk membantu section mengenai apa
              ini.
            </p>
          </div>

          <hr className='my-4 border-secondary' />

          <Row className='g-4 mb-3'>
            <Col lg={6}>
              {isInviteSubmitted ? (
                <div className='border-start border-2 border-primary ps-3'>
                  <div className='text-muted small fw-semibold mb-2'>
                    Hari, tanggal
                  </div>
                  <div className='text-dark fs-13 mb-0 lh-base'>
                    {formatEventDate(procurement?.invitationEventDate)}
                  </div>
                </div>
              ) : (
                <FormGroup className='mb-0'>
                  <Label className='form-label text-muted small fw-semibold mb-2'>
                    Hari, tanggal <span className='text-danger'>*</span>
                  </Label>
                  <Input
                    type='date'
                    name='eventDate'
                    value={invitationForm.eventDate}
                    onChange={onFormChange}
                    disabled={isSubmitting}
                    invalid={!!formErrors.eventDate}
                    innerRef={fieldRefs.eventDate}
                  />
                  {formErrors.eventDate && (
                    <div className='invalid-feedback d-block'>
                      {formErrors.eventDate}
                    </div>
                  )}
                </FormGroup>
              )}
            </Col>

            <Col lg={6}>
              {isInviteSubmitted ? (
                <div className='border-start border-2 border-primary ps-3'>
                  <div className='text-muted small fw-semibold mb-2'>
                    Waktu (Jakarta Local Time)
                  </div>
                  <div className='text-dark fs-13 mb-0 lh-base'>
                    {procurement?.invitationStartTime + ' WIB' || '-'} -{' '}
                    {procurement?.invitationTillEnd + ' WIB'
                      ? 'Hingga selesai'
                      : procurement?.invitationEndTime || '-'}
                  </div>
                </div>
              ) : (
                <FormGroup className='mb-0'>
                  <Label className='form-label text-muted small fw-semibold mb-2'>
                    Waktu (Jakarta Local Time){' '}
                    <span className='text-danger'>*</span>
                  </Label>

                  <Row className='g-2 align-items-start'>
                    <Col sm={4}>
                      <Flatpickr
                        key='invitation-start-time'
                        ref={fieldRefs.startTime}
                        id='invitation-start-time'
                        name='startTime'
                        className={`form-control ${
                          formErrors.startTime ? 'is-invalid' : ''
                        }`}
                        options={START_TIME_FLATPICKR_OPTIONS}
                        value={invitationForm.startTime?.substring(0, 5) || ''}
                        onChange={(_, dateStr) => {
                          onFormChange({
                            target: { name: 'startTime', value: dateStr },
                          });
                        }}
                        disabled={isSubmitting}
                      />
                      {formErrors.startTime && (
                        <div className='invalid-feedback d-block'>
                          {formErrors.startTime}
                        </div>
                      )}
                    </Col>

                    <Col
                      sm='auto'
                      className='d-none d-sm-flex align-items-center justify-content-center pt-2 px-0'
                    >
                      <span className='text-muted fw-bold'>-</span>
                    </Col>

                    <Col sm={4}>
                      <Flatpickr
                        key={`invitation-end-time-${untilFinished ? 'off' : 'on'}`}
                        ref={fieldRefs.endTime}
                        id='invitation-end-time'
                        name='endTime'
                        className={`form-control ${
                          formErrors.endTime ? 'is-invalid' : ''
                        } ${
                          untilFinished || isSubmitting
                            ? 'bg-light text-muted'
                            : ''
                        }`}
                        options={endTimeFlatpickrOptions}
                        value={
                          untilFinished
                            ? ''
                            : invitationForm.endTime?.substring(0, 5) || ''
                        }
                        onChange={(_, dateStr) => {
                          if (untilFinished || isSubmitting) return;
                          onFormChange({
                            target: { name: 'endTime', value: dateStr },
                          });
                        }}
                        disabled={untilFinished || isSubmitting}
                      />
                      {formErrors.endTime && (
                        <div className='invalid-feedback d-block'>
                          {formErrors.endTime}
                        </div>
                      )}
                    </Col>

                    <Col
                      sm='auto'
                      className='d-flex align-items-center ms-sm-2 pt-1 pt-sm-2'
                    >
                      <FormGroup switch className='mb-0'>
                        <Input
                          type='switch'
                          role='switch'
                          id='untilFinishedSwitchUX'
                          checked={untilFinished}
                          onChange={e => {
                            const isChecked = e.target.checked;
                            onUntilFinishedChange(isChecked);
                            if (isChecked) {
                              onFormChange({
                                target: { name: 'endTime', value: '' },
                              });
                            }
                          }}
                          disabled={isSubmitting}
                        />
                        <Label
                          check
                          htmlFor='untilFinishedSwitchUX'
                          className='small text-muted user-select-none'
                        >
                          Hingga selesai
                        </Label>
                      </FormGroup>
                    </Col>
                  </Row>
                </FormGroup>
              )}
            </Col>
          </Row>

          <Row className='g-4 mb-3'>
            <Col md={6}>
              {isInviteSubmitted ? (
                <div className='border-start border-2 border-primary ps-3'>
                  <div className='text-muted small fw-semibold mb-2'>
                    Location
                  </div>
                  <div className='text-dark fs-13 mb-0 lh-base'>
                    {procurement?.invitationLocation || '-'}
                  </div>
                </div>
              ) : (
                <FormGroup className='mb-0'>
                  <Label className='form-label text-muted small fw-semibold mb-2'>
                    Location <span className='text-danger'>*</span>
                  </Label>
                  <Input
                    type='text'
                    name='location'
                    value={invitationForm.location}
                    onChange={onFormChange}
                    disabled={isSubmitting}
                    invalid={!!formErrors.location}
                    placeholder='Isi tempat'
                    innerRef={fieldRefs.location}
                  />
                  {formErrors.location && (
                    <div className='invalid-feedback d-block'>
                      {formErrors.location}
                    </div>
                  )}
                </FormGroup>
              )}
            </Col>

            <Col md={6}>
              {isInviteSubmitted ? (
                <div className='border-start border-2 border-primary ps-3'>
                  <div className='text-muted small fw-semibold mb-2'>
                    Agenda
                  </div>
                  <div className='text-dark fs-13 mb-0 lh-base'>
                    {procurement?.invitationAgenda || '-'}
                  </div>
                </div>
              ) : (
                <FormGroup className='mb-0'>
                  <Label className='form-label text-muted small fw-semibold mb-2'>
                    Agenda <span className='text-danger'>*</span>
                  </Label>
                  <Input
                    type='text'
                    name='agenda'
                    value={invitationForm.agenda}
                    onChange={onFormChange}
                    disabled={isSubmitting}
                    invalid={!!formErrors.agenda}
                    placeholder='Isi agenda'
                    innerRef={fieldRefs.agenda}
                  />
                  {formErrors.agenda && (
                    <div className='invalid-feedback d-block'>
                      {formErrors.agenda}
                    </div>
                  )}
                </FormGroup>
              )}
            </Col>
          </Row>
        </Col>
      </Row>
    </CardBody>
  </Card>
);

const ProcurementInfoCard = ({ procurement, isPanTender }) => (
  <Card className='border shadow-md mb-4'>
    <CardBody>
      <Row>
        <Col xs={12}>
          <div className='mb-2'>
            <h5 className='fw-bold text-dark mb-1'>Procurement Information</h5>
            <p className='text-muted small'>
              Procurement details linked to this DMUT invitation.
            </p>
          </div>

          <hr className='my-4 border-secondary' />

          <Row className='g-3'>
            <InfoBlock
              label='Procurement Type'
              value={procurement?.procurementTypeHeaderLabel}
            />
            <InfoBlock
              label='Title Procurement'
              value={procurement?.titleProcurement}
            />
            <InfoBlock
              label='Proc Type'
              value={getProcurementTypeLabel(procurement?.procurementType)}
            />
            <InfoBlock
              label='Invitation Sent Date'
              value={
                formatDateToDDMMYYYY(
                  isPanTender
                    ? procurement?.isInvitePanitiaTenderCompletedDate
                    : procurement?.isInviteDMUTCompletedDate
                ) || '-'
              }
            />
            <InfoBlock
              label='Description'
              value={procurement?.description}
              col={12}
            />
          </Row>
        </Col>
      </Row>
    </CardBody>
  </Card>
);

const RequirementCard = ({
  procurement,
  parcels,
  isLoadingParcels,
  showProductSubtitle,
}) => {
  const periodProps = getContractPeriodProps(procurement);

  return (
    <Card className='border shadow-md mb-4'>
      <CardBody>
        <Row>
          <Col xs={12}>
            <div className='mb-4'>
              <h5 className='fw-bold text-dark mb-1'>Requirement</h5>
              <p className='text-muted small'>
                Contract and product requirements for this procurement.
              </p>
            </div>

            <hr className='my-2 border-secondary' />

            <Row className='g-3'>
              <Col xs={12}>
                <div className='mb-4'>
                  <h5 className='fw-bold text-dark mb-1'>Contract</h5>
                </div>
              </Col>
              <InfoBlock
                label='Contract Type'
                value={getContractTypeLabel(procurement?.contractType)}
              />
              <InfoBlock
                label={periodProps.startLabel}
                value={periodProps.startValue}
                col={3}
              />
              {periodProps.endLabel && (
                <InfoBlock
                  label={periodProps.endLabel}
                  value={periodProps.endValue}
                  col={3}
                />
              )}
              <InfoBlock
                label='Procurement No'
                value={procurement?.procurementNumber}
                col={3}
              />
            </Row>
          </Col>

          <hr className='my-4 border-secondary border-dashed' />

          <Col xs={12}>
            <Row className='g-3'>
              <Col xs={12}>
                <div className='mb-4'>
                  <h5 className='fw-bold text-dark mb-1'>
                    Product Specification/ Quantity
                  </h5>
                  {showProductSubtitle && (
                    <p className='text-muted small'>
                      Bisa jadi Kalimat tambahan untuk membantu section mengenai
                      apa ini .
                    </p>
                  )}
                </div>
              </Col>
              <InfoBlock
                label='Product Name'
                value={procurement?.productType?.product?.name}
              />
              <InfoBlock
                label='Product Type'
                value={procurement?.productType?.description}
              />
              <InfoBlock
                label='Operational Tolerance'
                value={procurement?.operationalTolerance + '%'}
              />
              <InfoBlock
                label='Operation Tolerance Options'
                value={procurement?.partyType}
              />
              <InfoBlock
                label='Product Specification'
                value={procurement?.productSpecification}
                col={12}
                isHtml
              />
            </Row>
          </Col>

          <hr className='my-4 border-secondary border-dashed' />

          <Col xs={12}>
            <div className='mb-4'>
              <h5 className='fw-bold text-dark mb-1'>
                Quantity & Delivery Time
              </h5>
            </div>

            <QuantityDeliverySection
              parcels={parcels}
              isLoading={isLoadingParcels}
              isLpg={isLpgProduct(procurement)}
              quantityUnitLabel={procurement?.productType?.unit}
            />

            <hr className='my-4 border-secondary border-dashed' />

            <Row className='mt-2 g-3'>
              <Col md={12}>
                <div className='border-start border-2 border-primary ps-3'>
                  <div className='text-muted small fw-semibold mb-2'>Notes</div>
                  <HtmlContent value={procurement?.globalNotes} />
                </div>
              </Col>
              <hr className='my-4 border-secondary border-dashed' />
              <Col md={12}>
                <div className='mt-2'>
                  <h5 className='fw-bold text-dark mb-3'>Port Restrictions</h5>
                  <PortRestrictionsTable
                    ports={procurement?.portRestriction}
                    productId={getProcurementProductNameId(procurement)}
                  />
                </div>
              </Col>
            </Row>
          </Col>
        </Row>
      </CardBody>
    </Card>
  );
};

const ClauseDetailsCard = ({ procurement }) => {
  const clauses = getVisibleProcurementClauses(procurement);

  return (
    <Card className='border shadow-md mb-4'>
      <CardBody>
        <div className='d-flex align-items-start justify-content-between gap-3 mb-4'>
          <div>
            <h5 className='fw-bold text-dark mb-1'>Clause Details</h5>
            <p className='text-muted small mb-0'>
              Review procurement clauses and requirements for this tender
            </p>
          </div>
          <span className='badge bg-primary-subtle text-primary rounded-pill fw-medium fs-12 flex-shrink-0'>
            {clauses.length} clauses
          </span>
        </div>

        {clauses.length > 0 ? (
          <div className='d-flex flex-column'>
            {clauses.map((clause, index) => (
              <ClauseBlock
                key={clause.id}
                index={index + 1}
                title={clause.clauseTitle}
                value={clause.clauseDescription}
                isMandatory={clause.isShowToDMUT === true}
              />
            ))}
          </div>
        ) : (
          <div className='text-center py-4 text-muted border rounded-3 bg-light-subtle'>
            No clause data available.
          </div>
        )}
      </CardBody>
    </Card>
  );
};

const PaymentComplianceCard = ({ procurement }) => {
  const [invoiceQuantityLabel, setInvoiceQuantityLabel] = useState(null);
  const [modeLabel, setModeLabel] = useState(null);

  useEffect(() => {
    const token = getAuthToken();
    const invoicingQuantityId = procurement?.invoicingQuantityId;
    const modeInvoicingQuantityId = procurement?.modeInvoicingQuantityId;
    let cancelled = false;

    const resolveLabels = async () => {
      if (!token) return;

      if (!procurement?.invoicingQuantity?.name && invoicingQuantityId) {
        try {
          const response = await fetch(`${api.API_URL}/invoicing-quantity`, {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          });
          const result = await response.json();
          const items = result?.data?.items || result?.data || [];
          const match = (Array.isArray(items) ? items : []).find(
            item => String(item?.id) === String(invoicingQuantityId)
          );
          if (!cancelled) setInvoiceQuantityLabel(match?.name ?? null);
        } catch {
          if (!cancelled) setInvoiceQuantityLabel(null);
        }
      } else if (!cancelled) {
        setInvoiceQuantityLabel(null);
      }

      if (
        !procurement?.modeInvoicingQuantity?.name &&
        invoicingQuantityId &&
        modeInvoicingQuantityId
      ) {
        try {
          const response = await fetch(
            `${api.API_URL}/invoicing-quantity/getAll-ModeInvoicingQuantity?InvoicingQuantityId=${invoicingQuantityId}`,
            {
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
              },
            }
          );
          const result = await response.json();
          const items = result?.data?.items || result?.data || [];
          const match = (Array.isArray(items) ? items : []).find(
            item => String(item?.id) === String(modeInvoicingQuantityId)
          );
          if (!cancelled) setModeLabel(match?.name ?? null);
        } catch {
          if (!cancelled) setModeLabel(null);
        }
      } else if (!cancelled) {
        setModeLabel(null);
      }
    };

    resolveLabels();
    return () => {
      cancelled = true;
    };
  }, [
    procurement?.invoicingQuantity?.name,
    procurement?.invoicingQuantityId,
    procurement?.modeInvoicingQuantity?.name,
    procurement?.modeInvoicingQuantityId,
  ]);

  return (
    <Card className='border shadow-md mb-4'>
      <CardBody>
        <Row>
          <Col xs={12}>
            <div className='mb-4'>
              <h5 className='fw-bold text-dark mb-1'>Payment Terms</h5>
              <p className='text-muted small'>
                Bisa jadi Kalimat tambahan untuk membantu section mengenai apa
                ini
              </p>

              <Row className='g-3'>
                <InfoBlock
                  label='Invoicing Quantity'
                  value={
                    procurement?.invoicingQuantity?.name || invoiceQuantityLabel
                  }
                />
                <InfoBlock
                  label='Mode'
                  value={procurement?.modeInvoicingQuantity?.name || modeLabel}
                />
                <InfoBlock
                  label='Currency'
                  value={getPaymentCurrencyLabel(procurement?.paymentCurrency)}
                />
                <Col xs={12}>
                  <div className='border-start border-2 border-primary ps-3'>
                    <div className='text-muted small fw-semibold mb-2'>
                      Description of Payment Term
                    </div>
                    <HtmlContent
                      value={procurement?.descriptionOfPaymentTerms}
                    />
                  </div>
                </Col>
              </Row>
            </div>
          </Col>

          <hr className='my-4 border-secondary border-dashed' />

          <Col xs={12}>
            <div className='mb-4'>
              <h5 className='fw-bold text-dark mb-1'>Compliance</h5>
              <p className='text-muted small'>
                Bisa jadi Kalimat tambahan untuk membantu section mengenai apa
                ini
              </p>

              <Row className='g-3'>
                <InfoBlock label='Law' value={procurement?.law?.name} col={6} />
                <InfoBlock
                  label='Arbitration'
                  value={procurement?.arbitration?.name}
                  col={6}
                />
                <Col xs={12}>
                  <div className='border-start border-2 border-primary ps-3'>
                    <div className='text-muted small fw-semibold mb-2'>
                      Other Terms & Condition
                    </div>
                    <HtmlContent value={procurement?.otherTermsAndCondition} />
                  </div>
                </Col>
                <Col xs={12}>
                  <div className='border-start border-2 border-primary ps-3'>
                    <div className='text-muted small fw-semibold mb-2'>
                      Additional Notes
                    </div>
                    <HtmlContent value={procurement?.additionalNotes} />
                  </div>
                </Col>
              </Row>
            </div>
          </Col>
        </Row>
      </CardBody>
    </Card>
  );
};

const ValidityCard = ({ procurement }) => (
  <Card className='border shadow-md mb-4'>
    <CardBody>
      <Row>
        <Col xs={12}>
          <div>
            <h5 className='fw-bold text-dark mb-1'>Validity</h5>
            <p className='text-muted small'>
              Bisa jadi Kalimat tambahan untuk membantu section mengenai apa ini
            </p>

            <Row className='g-3'>
              <InfoBlock
                label='Submission Deadline (Jakarta Local Time)'
                value={formatProposalDeadline(
                  procurement?.proposalSubmissionDeadline
                )}
                col={6}
              />
              <InfoBlock
                label='Validity Period (Jakarta Local Time)'
                value={formatProposalDeadline(
                  procurement?.proposalValidityPeriodMax
                )}
                col={6}
              />
            </Row>
          </div>
        </Col>
      </Row>
    </CardBody>
  </Card>
);

const Requirement = forwardRef(
  (
    {
      variant = 'dmut',
      procurement,
      parcels = [],
      isLoadingParcels,
      isInviteSubmitted = false,
      isSubmitting = false,
      onSendInvitation,
    },
    ref
  ) => {
    const isPanTender = variant === 'panTender';

    const [untilFinished, setUntilFinished] = useState(
      procurement?.invitationTillEnd || false
    );
    const [invitationForm, setInvitationForm] = useState({
      eventDate: procurement?.invitationEventDate
        ? procurement.invitationEventDate.split('T')[0]
        : '',
      startTime: procurement?.invitationStartTime || '',
      endTime: procurement?.invitationEndTime || '',
      tillEnd: procurement?.invitationTillEnd ?? true,
      location: procurement?.invitationLocation || '',
      agenda: procurement?.invitationAgenda || '',
    });
    const [formErrors, setFormErrors] = useState({});
    const fieldRefs = {
      eventDate: useRef(null),
      startTime: useRef(null),
      endTime: useRef(null),
      location: useRef(null),
      agenda: useRef(null),
    };

    useEffect(() => {
      if (!isPanTender || !procurement) return;

      setInvitationForm({
        eventDate: procurement.invitationEventDate
          ? procurement.invitationEventDate.split('T')[0]
          : '',
        startTime: procurement.invitationStartTime || '',
        endTime: procurement.invitationEndTime || '',
        tillEnd: procurement.invitationTillEnd ?? true,
        location: procurement.invitationLocation || '',
        agenda: procurement.invitationAgenda || '',
      });
      setUntilFinished(procurement.invitationTillEnd || false);
    }, [isPanTender, procurement]);

    const handleFormChange = e => {
      const { name, value, type, checked } = e.target;
      setInvitationForm(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      }));
      setFormErrors(prev => ({ ...prev, [name]: undefined }));
    };

    const endTimeFlatpickrOptions = useMemo(
      () => ({
        ...END_TIME_FLATPICKR_OPTIONS,
        clickOpens: !(untilFinished || isSubmitting),
      }),
      [untilFinished, isSubmitting]
    );

    useEffect(() => {
      if (!isPanTender) return;

      const fpInstance = fieldRefs.endTime.current;
      const input = fpInstance?.input;
      if (!input) return;

      const shouldDisable = untilFinished || isSubmitting;
      input.disabled = shouldDisable;

      if (shouldDisable) {
        fpInstance.close?.();
      }
      // fieldRefs.endTime is stable
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      isPanTender,
      untilFinished,
      isSubmitting,
      invitationForm.startTime,
      endTimeFlatpickrOptions,
    ]);

    const validateInvitationForm = () => {
      const errors = {};

      if (!invitationForm.eventDate) {
        errors.eventDate = 'Event Date is required';
      }
      if (!invitationForm.startTime) {
        errors.startTime = 'Start Time is required';
      }
      if (!untilFinished && !invitationForm.endTime) {
        errors.endTime = 'End Time is required when not "Hingga selesai"';
      }
      if (
        !untilFinished &&
        invitationForm.startTime &&
        invitationForm.endTime &&
        invitationForm.startTime.substring(0, 5) ===
          invitationForm.endTime.substring(0, 5)
      ) {
        errors.endTime = 'End Time must be different from Start Time';
      }
      if (!invitationForm.location?.trim()) {
        errors.location = 'Location is required';
      }
      if (!invitationForm.agenda?.trim()) {
        errors.agenda = 'Agenda is required';
      }

      setFormErrors(errors);

      if (Object.keys(errors).length > 0) {
        const firstField = FIELD_ORDER.find(key => errors[key]);
        focusFieldRef(fieldRefs[firstField]);
        toast.error('Please complete all required fields.');
        return false;
      }

      return true;
    };

    const buildInvitationPayload = () => ({
      eventDate: invitationForm.eventDate || null,
      startTime: invitationForm.startTime || null,
      endTime: untilFinished ? null : invitationForm.endTime || null,
      tillEnd: untilFinished,
      location: invitationForm.location || null,
      agenda: invitationForm.agenda || null,
    });

    const handleSendInvitation = async () => {
      if (isInviteSubmitted) return false;
      if (!validateInvitationForm()) return false;

      await onSendInvitation(buildInvitationPayload());
      return true;
    };

    useImperativeHandle(
      ref,
      () =>
        isPanTender
          ? {
              validateInvitation: validateInvitationForm,
              submitInvitation: handleSendInvitation,
              getInvitationPayload: buildInvitationPayload,
            }
          : {},
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [isPanTender, invitationForm, untilFinished, isInviteSubmitted]
    );

    return (
      <React.Fragment>
        {isPanTender && (
          <InvitationDetailCard
            procurement={procurement}
            isInviteSubmitted={isInviteSubmitted}
            isSubmitting={isSubmitting}
            invitationForm={invitationForm}
            formErrors={formErrors}
            fieldRefs={fieldRefs}
            untilFinished={untilFinished}
            endTimeFlatpickrOptions={endTimeFlatpickrOptions}
            onFormChange={handleFormChange}
            onUntilFinishedChange={setUntilFinished}
          />
        )}

        <ProcurementInfoCard
          procurement={procurement}
          isPanTender={isPanTender}
        />

        <RequirementCard
          procurement={procurement}
          parcels={parcels}
          isLoadingParcels={isLoadingParcels}
          showProductSubtitle={!isPanTender}
        />

        <ClauseDetailsCard procurement={procurement} />

        <PaymentComplianceCard procurement={procurement} />

        <ValidityCard procurement={procurement} />
      </React.Fragment>
    );
  }
);

Requirement.displayName = 'Requirement';

export default Requirement;
